// src/api/files.api.ts — SCE Issues
// Upload et listing des pièces jointes dans la doc library SCE_PiecesJointes.
//
// ─── Stratégie de stockage ───
//
// Chaque problème a son propre sous-dossier dans la doc library :
//   /SCE_PiecesJointes/P2026-0042/photo.jpg
//   /SCE_PiecesJointes/P2026-0042/schema.pdf
//
// L'URL du dossier est construite depuis le driveId (VITE_DRIVE_PIECES_JOINTES_ID).
// Graph API crée le dossier automatiquement si absent lors du premier upload.
//
// ─── Stratégie d'upload ───
//
// < 4 MB   → PUT simple (1 requête, ~instantané)
// ≥ 4 MB   → Upload session en chunks de 3 MB (obligatoire au-delà de 4 MB
//             selon la limitation Graph API, recommandé dès 3 MB pour la fiabilité)
//
// ─── Types autorisés ───
//
// Photos : jpg, jpeg, png, heic, webp
// PDF    : pdf
// Vidéos : mp4, mov, webm
//
// ─── Règles métier ───
//
// PJ permanentes : pas de suppression implémentée (design volontaire).
// Pas de limite de quantité par problème.
// 25 MB max par fichier.

import { graphFetch, graphGet } from './graphClient'
import { nommerAnnotation, nommerPourCommentaire } from '@/utils/rattachementPhotos'

// ─── Configuration ───

const DRIVE_ID = import.meta.env.VITE_DRIVE_PIECES_JOINTES_ID ?? ''
const CHUNK_SIZE = 3 * 1024 * 1024      // 3 MB par chunk
const MAX_FILE_SIZE = 25 * 1024 * 1024  // 25 MB max par fichier

// ─── Types de fichiers autorisés ───

export const ACCEPTED_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'heic', 'webp',   // photos
  'pdf',                                    // documents
  'mp4', 'mov', 'webm',                    // vidéos
] as const

export const ACCEPTED_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/heic', 'image/heif',
  'image/webp',
  'application/pdf',
  'video/mp4', 'video/quicktime', 'video/webm',
]

// Accept string pour <input type="file">
export const INPUT_ACCEPT = ACCEPTED_MIME_TYPES.join(',')

// ─── Types publics ───

export interface PieceJointe {
  id: string           // ID Graph de l'item (stable, pour key React)
  nom: string          // Nom du fichier
  url: string          // URL de téléchargement direct (non authentifiée, expirable)
  webUrl: string       // URL SharePoint pour ouvrir dans le navigateur
  /**
   * Aperçus générés par SharePoint (images) : `vignette` ≈ 176 px, `apercu`
   * ≈ 800 px. Adresses faites pour <img>, sans session SharePoint, servies en
   * JPEG — HEIC compris. Absents juste après un envoi (générés en différé) :
   * repli sur `url`. Cf. PieceJointeGallery.
   */
  vignette?: string
  apercu?: string
  taille: number       // Octets
  mimeType: string     // Type MIME
  dateUpload: string   // ISO 8601
  isImage: boolean     // true si image préviewable
  isVideo: boolean     // true si vidéo
  isPdf: boolean       // true si PDF
}

export interface UploadResult {
  success: boolean
  fichier?: PieceJointe
  erreur?: string
}

// ─── Erreur de validation ───

export class FileValidationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FileValidationError'
  }
}

// ─── Structure brute Graph API ───

interface DriveItemResponse {
  id: string
  name: string
  size: number
  file?: { mimeType: string }
  createdDateTime?: string
  webUrl: string
  '@microsoft.graph.downloadUrl'?: string
  thumbnails?: { medium?: { url?: string }; large?: { url?: string } }[]
}

interface DriveItemListResponse {
  value: DriveItemResponse[]
  '@odata.nextLink'?: string
}

interface UploadSessionResponse {
  uploadUrl: string
  expirationDateTime: string
}

// ─── Helpers ───

function driveItemPath(issueId: string, fileName: string): string {
  const encoded = encodeURIComponent(fileName)
  return `/drives/${DRIVE_ID}/root:/${issueId}/${encoded}:`
}

function driveFolderChildrenPath(issueId: string): string {
  return `/drives/${DRIVE_ID}/root:/${issueId}:/children`
}

function mapDriveItem(item: DriveItemResponse): PieceJointe {
  const mime = item.file?.mimeType ?? ''
  const apercus = item.thumbnails?.[0]
  return {
    id: item.id,
    nom: item.name,
    url: item['@microsoft.graph.downloadUrl'] ?? item.webUrl,
    webUrl: item.webUrl,
    ...(apercus?.medium?.url ? { vignette: apercus.medium.url } : {}),
    ...(apercus?.large?.url ? { apercu: apercus.large.url } : {}),
    taille: item.size,
    mimeType: mime,
    dateUpload: item.createdDateTime ?? '',
    isImage: mime.startsWith('image/'),
    isVideo: mime.startsWith('video/'),
    isPdf: mime === 'application/pdf',
  }
}

/**
 * Validation côté client avant tout upload.
 * @throws {FileValidationError} si le fichier n'est pas accepté
 */
export function validateFile(file: File): void {
  if (file.size > MAX_FILE_SIZE) {
    const mb = Math.round(file.size / 1024 / 1024)
    throw new FileValidationError(
      `"${file.name}" est trop volumineux (${mb} MB). Maximum : 25 MB.`,
    )
  }

  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  if (!ACCEPTED_EXTENSIONS.includes(ext as typeof ACCEPTED_EXTENSIONS[number])) {
    throw new FileValidationError(
      `"${file.name}" : type de fichier non autorisé (.${ext}). ` +
      `Formats acceptés : photos, PDF, vidéos (mp4, mov, webm).`,
    )
  }
}

// ─── Upload simple (< 4 MB) ───

async function uploadSimple(
  issueId: string,
  file: File,
): Promise<DriveItemResponse> {
  const arrayBuffer = await file.arrayBuffer()
  return graphFetch<DriveItemResponse>(`${driveItemPath(issueId, file.name)}/content`, {
    method: 'PUT',
    rawBody: arrayBuffer,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  })
}

// ─── Upload session en chunks (≥ 4 MB) ───

async function uploadChunked(
  issueId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<DriveItemResponse> {
  // 1. Créer la session
  const session = await graphFetch<UploadSessionResponse>(
    `${driveItemPath(issueId, file.name)}/createUploadSession`,
    {
      method: 'POST',
      body: {
        item: {
          '@microsoft.graph.conflictBehavior': 'rename',
          name: file.name,
        },
      },
    },
  )

  const uploadUrl = session.uploadUrl
  const totalSize = file.size
  let offset = 0
  let lastResponse: DriveItemResponse | null = null

  // 2. Envoyer les chunks
  while (offset < totalSize) {
    const end = Math.min(offset + CHUNK_SIZE, totalSize)
    const chunk = file.slice(offset, end)
    const chunkBuffer = await chunk.arrayBuffer()

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Length': String(end - offset),
        'Content-Range': `bytes ${offset}-${end - 1}/${totalSize}`,
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: chunkBuffer,
    })

    if (response.status === 202) {
      // Chunk accepté, pas encore terminé
      onProgress?.(Math.round((end / totalSize) * 100))
    } else if (response.status === 201 || response.status === 200) {
      // Upload terminé
      lastResponse = (await response.json()) as DriveItemResponse
      onProgress?.(100)
    } else {
      const details = await response.text().catch(() => '')
      throw new Error(
        `Erreur upload chunk (${response.status}) : ${details || 'erreur inconnue'}`,
      )
    }

    offset = end
  }

  if (!lastResponse) {
    throw new Error('Upload terminé mais aucune réponse finale reçue')
  }
  return lastResponse
}

// ─── API publique ───

/**
 * Upload un seul fichier vers le sous-dossier de l'issue dans SCE_PiecesJointes.
 *
 * Gère automatiquement le choix PUT simple vs upload session selon la taille.
 * Le sous-dossier est créé automatiquement si absent (comportement Graph natif).
 *
 * @param onProgress Callback de progression 0-100 (optionnel, chunked only)
 */
export async function uploadFile(
  issueId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<PieceJointe> {
  validateFile(file)

  // Unicité du nom : si conflit, Graph renomme automatiquement (conflictBehavior: rename)
  // pour les uploads session. Pour PUT simple, on horodate le nom pour éviter l'écrasement.
  const safeFile =
    file.size < 4 * 1024 * 1024
      ? new File([file], sanitizeFileName(file.name), { type: file.type })
      : file

  const raw =
    file.size < 4 * 1024 * 1024
      ? await uploadSimple(issueId, safeFile)
      : await uploadChunked(issueId, safeFile, onProgress)

  return mapDriveItem(raw)
}

/**
 * Upload plusieurs fichiers en séquence (pas en parallèle pour éviter la
 * surcharge réseau sur mobile).
 *
 * Retourne les résultats de chaque upload — les échecs individuels n'arrêtent
 * pas les suivants.
 *
 * @param onFileProgress Callback (fileIndex, pct) — progression par fichier
 * @param commentaireId Événement auquel les fichiers appartiennent : inscrit
 *   dans leur nom (cf. utils/rattachementPhotos). Absent : photos du problème.
 */
export async function uploadFiles(
  issueId: string,
  files: File[],
  onFileProgress?: (fileIndex: number, pct: number) => void,
  commentaireId?: string,
): Promise<UploadResult[]> {
  const results: UploadResult[] = []

  for (let i = 0; i < files.length; i++) {
    const brut = files[i]
    if (!brut) continue
    const file = commentaireId
      ? new File([brut], nommerPourCommentaire(brut.name, commentaireId), { type: brut.type })
      : brut
    try {
      const pj = await uploadFile(
        issueId,
        file,
        (pct) => onFileProgress?.(i, pct),
      )
      results.push({ success: true, fichier: pj })
    } catch (err) {
      results.push({
        success: false,
        erreur:
          err instanceof Error ? err.message : `Erreur upload "${file.name}"`,
      })
    }
  }

  return results
}

/**
 * Envoie des fichiers et, pour chaque photo annotée avant l'envoi, sa version
 * annotée juste après l'original : elle porte l'identifiant de l'original
 * (cf. utils/rattachementPhotos). Un résultat par fichier ; une annotation
 * non envoyée compte comme un échec, l'original restant envoyé.
 */
export async function envoyerFichiers(
  issueId: string,
  fichiers: { file: File; annotation?: Blob }[],
  onFileProgress?: (fileIndex: number, pct: number) => void,
  commentaireId?: string,
): Promise<UploadResult[]> {
  const results: UploadResult[] = []
  for (let i = 0; i < fichiers.length; i++) {
    const f = fichiers[i]
    if (!f) continue
    const [r] = await uploadFiles(issueId, [f.file], (_, pct) => onFileProgress?.(i, pct), commentaireId)
    if (!r) continue
    results.push(r)
    if (r.success && r.fichier && f.annotation) {
      const annotee = new File([f.annotation], nommerAnnotation(r.fichier.nom, r.fichier.id), { type: 'image/jpeg' })
      const [ra] = await uploadFiles(issueId, [annotee], undefined, commentaireId)
      if (ra && !ra.success) results.push(ra)
    }
  }
  return results
}

/**
 * Le contenu d'une pièce jointe, pour l'annoter. L'adresse de téléchargement
 * reçue avec la liste expire (≈ 1 h, fiche restée ouverte) et manque parfois
 * sur iPhone : on la redemande à Graph juste avant, comme pour les photos de
 * profil (api/annuaire, validé PC et iPhone).
 */
/**
 * Côté long de l'aperçu `large` de SharePoint. Une image qui le remplit a été
 * réduite : l'original est plus grand (capture d'écran, photo).
 */
export const COTE_APERCU = 800

/**
 * Économie de données demandée, ou réseau très lent : on s'en tient aux
 * aperçus. Safari ne dit rien de la connexion → qualité maximale.
 */
export function connexionLente(): boolean {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection
  return !!c && (c.saveData === true || c.effectiveType === 'slow-2g' || c.effectiveType === '2g')
}

export async function lireContenu(pj: PieceJointe): Promise<Blob> {
  const item = await graphGet<DriveItemResponse>(`/drives/${DRIVE_ID}/items/${pj.id}`)
  const adresse = item['@microsoft.graph.downloadUrl']
  if (!adresse) throw new Error('Adresse de téléchargement absente')
  const r = await fetch(adresse)
  if (!r.ok) throw new Error(`Téléchargement refusé (${r.status})`)
  return r.blob()
}

/**
 * Liste les pièces jointes d'un problème depuis son sous-dossier SP.
 *
 * Retourne un tableau vide si le dossier n'existe pas encore (pas d'erreur —
 * c'est le cas normal pour un problème sans PJ).
 */
export async function listPiecesJointes(issueId: string): Promise<PieceJointe[]> {
  try {
    const response = await graphGet<DriveItemListResponse>(
      // Aperçus (thumbnails) : ce que l'iPhone sait afficher. L'adresse de
      // téléchargement ne suffit pas — sans session SharePoint (repli sur
      // webUrl) ou servie « à télécharger », Safari montre une image cassée.
      `${driveFolderChildrenPath(issueId)}?$select=id,name,size,file,createdDateTime,webUrl,@microsoft.graph.downloadUrl&$expand=thumbnails&$top=50`,
    )
    return response.value
      .filter((item) => item.file) // exclut les sous-dossiers
      .map(mapDriveItem)
      .sort((a, b) => a.dateUpload.localeCompare(b.dateUpload))
  } catch (err: unknown) {
    // 404 = dossier inexistant → pas de PJ encore, c'est normal
    if (
      err &&
      typeof err === 'object' &&
      'status' in err &&
      (err as { status: number }).status === 404
    ) {
      return []
    }
    throw err
  }
}

// ─── Helpers internes ───

/**
 * Préfixe le nom de fichier avec un timestamp pour garantir l'unicité
 * lors des uploads simples (PUT écrase le fichier existant).
 *
 * "photo.jpg" → "20260529-143022-photo.jpg"
 */
function sanitizeFileName(name: string): string {
  const ts = new Date()
    .toISOString()
    .replace(/[-:]/g, '')
    .replace('T', '-')
    .slice(0, 15)
  return `${ts}-${name}`
}
